import { useEffect, useMemo, useState } from 'react';
import { FaEnvelope, FaPhone, FaWhatsapp } from 'react-icons/fa6';

import ContactMethodsEditor from '../../components/ContactMethodsEditor';
import ActionMenu from '../../components/ui/ActionMenu';
import FormModal from '../../components/ui/FormModal';
import Modal from '../../components/ui/Modal';
import StatusMessage from '../../components/ui/StatusMessage';
import {
  createSupplierContact,
  deleteSupplierContact,
  setPrimarySupplierContact,
  subscribeToSupplierContacts,
  updateSupplierContact,
} from '../../services/supplierContactsService';
import {
  createEmptyContactMethod,
  getPrimaryContactMethod,
  normalizeContactMethods,
  normalizeDialNumber,
  normalizeWhatsappNumber,
} from '../../utils/contactMethods';

const createEmptyContactForm = () => ({
  name: '',
  role: '',
  contactMethods: [createEmptyContactMethod({ label: 'Mobile' })],
  email: '',
});

const normalizeContactForm = (contact = {}) => ({
  name: typeof contact?.name === 'string' ? contact.name : '',
  role: typeof contact?.role === 'string' ? contact.role : '',
  contactMethods: normalizeContactMethods(contact, {
    numberKeys: ['contactNumber', 'phone'],
    whatsappKeys: ['contactWhatsapp', 'whatsapp'],
    defaultLabel: 'Mobile',
    requireAtLeastOne: true,
  }),
  email: typeof contact?.email === 'string' ? contact.email : '',
});

const getDisplayValue = (value) =>
  typeof value === 'string' && value.trim() ? value.trim() : '-';

export default function SupplierContactsSection({ supplierId, canManage = false }) {
  const [contacts, setContacts] = useState([]);
  const [isLoadingContacts, setIsLoadingContacts] = useState(true);
  const [contactsError, setContactsError] = useState('');
  const [contactsMessage, setContactsMessage] = useState('');
  const [isContactModalOpen, setIsContactModalOpen] = useState(false);
  const [editingContact, setEditingContact] = useState(null);
  const [contactForm, setContactForm] = useState(createEmptyContactForm);
  const [contactFormError, setContactFormError] = useState('');
  const [isSubmittingContact, setIsSubmittingContact] = useState(false);
  const [contactPendingDelete, setContactPendingDelete] = useState(null);
  const [isDeletingContact, setIsDeletingContact] = useState(false);
  const [primaryContactIdInProgress, setPrimaryContactIdInProgress] = useState('');

  useEffect(() => {
    setIsLoadingContacts(true);
    setContactsError('');
    setContactsMessage('');

    const unsubscribe = subscribeToSupplierContacts(
      supplierId,
      (nextContacts) => {
        setContacts(Array.isArray(nextContacts) ? nextContacts : []);
        setIsLoadingContacts(false);
      },
      (error) => {
        console.error('Error fetching supplier contacts:', error);
        setContacts([]);
        setContactsError('Failed to load contacts.');
        setIsLoadingContacts(false);
      }
    );

    return unsubscribe;
  }, [supplierId]);

  const primaryContactId = useMemo(
    () => contacts.find((contact) => contact?.isPrimary === true)?.id || '',
    [contacts]
  );

  const clearFeedback = () => {
    setContactsError('');
    setContactsMessage('');
  };

  const resetContactForm = () => {
    setContactForm(createEmptyContactForm());
    setContactFormError('');
    setIsSubmittingContact(false);
  };

  const handleOpenAddContactModal = () => {
    clearFeedback();
    setEditingContact(null);
    resetContactForm();
    setIsContactModalOpen(true);
  };

  const handleOpenEditContactModal = (contact) => {
    clearFeedback();
    setEditingContact(contact);
    setContactForm(normalizeContactForm(contact));
    setContactFormError('');
    setIsContactModalOpen(true);
  };

  const handleCloseContactModal = () => {
    if (isSubmittingContact) {
      return;
    }

    setIsContactModalOpen(false);
    setEditingContact(null);
    resetContactForm();
  };

  const handleContactFormChange = (field, value) => {
    setContactForm((currentForm) => ({
      ...currentForm,
      [field]: value,
    }));

    if (contactFormError) {
      setContactFormError('');
    }
  };

  const handleSubmitContact = async (event) => {
    event.preventDefault();

    const trimmedName = contactForm.name.trim();

    if (!trimmedName) {
      setContactFormError('Name is required.');
      return;
    }

    const invalidContactMethod = (contactForm.contactMethods || [])
      .find((method) => !String(method?.number || '').trim());

    if (invalidContactMethod) {
      setContactFormError('Contact method number is required.');
      return;
    }

    setIsSubmittingContact(true);
    setContactFormError('');

    try {
      const payload = {
        ...contactForm,
        name: trimmedName,
      };

      if (editingContact?.id) {
        await updateSupplierContact(editingContact.id, payload);
        setContactsMessage('Contact updated.');
      } else {
        await createSupplierContact(supplierId, payload);
        setContactsMessage('Contact added.');
      }

      setContactsError('');
      setIsContactModalOpen(false);
      setEditingContact(null);
      resetContactForm();
    } catch (error) {
      console.error('Error saving supplier contact:', error);
      setContactFormError(error?.message || 'Failed to save contact.');
      setIsSubmittingContact(false);
    }
  };

  const handleOpenDeleteModal = (contact) => {
    clearFeedback();
    setContactPendingDelete(contact);
  };

  const handleCloseDeleteModal = () => {
    if (isDeletingContact) {
      return;
    }

    setContactPendingDelete(null);
  };

  const handleConfirmDelete = async () => {
    if (isDeletingContact || !contactPendingDelete?.id) {
      return;
    }

    setIsDeletingContact(true);

    try {
      await deleteSupplierContact(contactPendingDelete.id);
      setContactsMessage('Contact deleted.');
      setContactsError('');
      setContactPendingDelete(null);
      setIsDeletingContact(false);
    } catch (error) {
      console.error('Error deleting supplier contact:', error);
      setContactsError(error?.message || 'Failed to delete contact.');
      setIsDeletingContact(false);
    }
  };

  const handleSetPrimary = async (contact) => {
    if (!contact?.id || contact.id === primaryContactId || primaryContactIdInProgress) {
      return;
    }

    clearFeedback();
    setPrimaryContactIdInProgress(contact.id);

    try {
      await setPrimarySupplierContact(supplierId, contact.id);
      setContactsMessage('Primary contact updated.');
      setContactsError('');
      setPrimaryContactIdInProgress('');
    } catch (error) {
      console.error('Error setting primary supplier contact:', error);
      setContactsError(error?.message || 'Failed to set primary contact.');
      setPrimaryContactIdInProgress('');
    }
  };

  const sectionHeaderStyle = {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    gap: 16,
    flexWrap: 'wrap',
    marginBottom: 12,
  };

  const tableWrapStyle = {};

  const tableStyle = {
    width: '100%',
    tableLayout: 'fixed',
  };

  const tableBodyCellStyle = {
    color: '#334155',
    fontSize: 14,
    verticalAlign: 'middle',
  };

  const primaryCellStyle = {
    ...tableBodyCellStyle,
    width: 105,
  };

  const emptyStateStyle = {
    margin: 0,
    color: '#64748b',
    lineHeight: 1.6,
  };

  const fieldWrapStyle = {
    display: 'flex',
    flexDirection: 'column',
    gap: 8,
    marginBottom: 14,
  };

  const labelStyle = {
    fontSize: 14,
    fontWeight: 600,
    color: '#0f172a',
  };

  const inputBaseStyle = {
    width: '100%',
    border: '1px solid #cbd5e1',
    borderRadius: 12,
    padding: '12px 14px',
    fontSize: 14,
    color: '#0f172a',
    backgroundColor: '#ffffff',
    boxSizing: 'border-box',
    outline: 'none',
    fontFamily: 'inherit',
  };

  const primaryRowCellStyle = {
    backgroundColor: '#f8fafc',
  };

  const modalTitle = editingContact?.id ? 'Edit Contact' : 'Add Contact';

  const renderContactCommunicationLink = (value, type, displayText) => {
    const displayValue = getDisplayValue(value);

    if (displayValue === '-') {
      return displayValue;
    }

    const hrefByType = {
      phone: `tel:${normalizeDialNumber(displayValue)}`,
      whatsapp: `https://wa.me/${normalizeWhatsappNumber(displayValue)}`,
      email: `mailto:${displayValue}`,
    };
    const titleByType = {
      phone: 'Call',
      whatsapp: 'Open WhatsApp',
      email: 'Send Email',
    };
    const iconByType = {
      phone: FaPhone,
      whatsapp: FaWhatsapp,
      email: FaEnvelope,
    };
    const Icon = iconByType[type];

    return (
      <a
        href={hrefByType[type]}
        target={type === 'whatsapp' ? '_blank' : undefined}
        rel={type === 'whatsapp' ? 'noreferrer' : undefined}
        className={`interactive-text-link contact-comm-link contact-comm-link--${type}`}
        aria-label={`${titleByType[type]} ${displayValue}`}
        title={titleByType[type]}
      >
        <Icon aria-hidden="true" className="interactive-link-icon" />
        {displayText || displayValue}
      </a>
    );
  };

  return (
    <section className="surface-card surface-card--section">
      <div className="section-header" style={sectionHeaderStyle}>
        <h2 className="section-title">Contacts</h2>

        {canManage ? (
          <button
            type="button"
            className="btn btn-primary"
            onClick={handleOpenAddContactModal}
          >
            Add Contact
          </button>
        ) : null}
      </div>

      <StatusMessage tone="success" message={contactsMessage} />
      <StatusMessage tone="error" message={contactsError} />

      {isLoadingContacts ? (
        <p style={emptyStateStyle}>Loading contacts...</p>
      ) : contacts.length === 0 ? (
        <p style={emptyStateStyle}>No contact people have been added yet.</p>
      ) : (
        <div className="table-container contact-table-container" style={tableWrapStyle}>
          <table className="data-table contact-table" style={tableStyle}>
            <thead>
              <tr>
                <th>Name</th>
                <th>Role</th>
                <th>Primary Method</th>
                <th>Email</th>
                <th>Primary</th>
                <th>Actions</th>
              </tr>
            </thead>
            <tbody>
              {contacts.map((contact) => {
                const isPrimary = contact.id === primaryContactId;
                const isPrimaryActionPending = primaryContactIdInProgress === contact.id;
                const primaryMethod = getPrimaryContactMethod(contact.contactMethods);
                const cellStyle = isPrimary
                  ? { ...tableBodyCellStyle, ...primaryRowCellStyle }
                  : tableBodyCellStyle;

                return (
                  <tr key={contact.id}>
                    <td style={cellStyle}>
                      <strong>{getDisplayValue(contact.name)}</strong>
                    </td>
                    <td style={cellStyle}>{getDisplayValue(contact.role)}</td>
                    <td style={cellStyle}>
                      {primaryMethod ? (
                        <div className="contact-primary-method-cell">
                          <strong>
                            <FaPhone
                              aria-hidden="true"
                              className="contact-primary-method-cell__icon"
                            />
                            {primaryMethod.label || 'Other'}
                          </strong>
                          <span>{primaryMethod.number}</span>
                          <span className="contact-primary-method-cell__actions">
                            {renderContactCommunicationLink(primaryMethod.number, 'phone', 'Call')}
                            {primaryMethod.hasWhatsapp
                              ? renderContactCommunicationLink(primaryMethod.number, 'whatsapp', 'WhatsApp')
                              : null}
                          </span>
                        </div>
                      ) : (
                        '-'
                      )}
                    </td>
                    <td style={cellStyle}>
                      {renderContactCommunicationLink(contact.email, 'email')}
                    </td>
                    <td
                      style={
                        isPrimary
                          ? { ...primaryCellStyle, ...primaryRowCellStyle }
                          : primaryCellStyle
                      }
                    >
                      {isPrimary ? <span className="tag">Primary</span> : '-'}
                    </td>
                    <td style={cellStyle}>
                      {canManage ? (
                        <ActionMenu
                          items={[
                            {
                              key: 'edit',
                              label: 'Edit',
                              onSelect: () => handleOpenEditContactModal(contact),
                            },
                            {
                              key: 'set-primary',
                              label: isPrimaryActionPending ? 'Setting primary...' : 'Set Primary',
                              onSelect: () => handleSetPrimary(contact),
                              disabled: isPrimary || Boolean(primaryContactIdInProgress),
                            },
                            {
                              key: 'delete',
                              label: 'Delete',
                              onSelect: () => handleOpenDeleteModal(contact),
                              tone: 'danger',
                              disabled: isDeletingContact,
                            },
                          ]}
                        />
                      ) : (
                        '-'
                      )}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}

      {canManage ? (
        <FormModal
          isOpen={isContactModalOpen}
          onClose={handleCloseContactModal}
          onSubmit={handleSubmitContact}
          title={modalTitle}
          submitLabel={isSubmittingContact ? 'Saving...' : 'Save Contact'}
          submitDisabled={isSubmittingContact}
          cancelDisabled={isSubmittingContact}
          errorMessage={contactFormError}
        >
          <label style={fieldWrapStyle}>
            <span style={labelStyle}>Name *</span>
            <input
              name="name"
              type="text"
              value={contactForm.name}
              onChange={(event) => handleContactFormChange('name', event.target.value)}
              required
              disabled={isSubmittingContact}
              style={inputBaseStyle}
            />
          </label>

          <label style={fieldWrapStyle}>
            <span style={labelStyle}>Role</span>
            <input
              name="role"
              type="text"
              value={contactForm.role}
              onChange={(event) => handleContactFormChange('role', event.target.value)}
              disabled={isSubmittingContact}
              style={inputBaseStyle}
            />
          </label>

          <ContactMethodsEditor
            value={contactForm.contactMethods}
            onChange={(contactMethods) => handleContactFormChange('contactMethods', contactMethods)}
            disabled={isSubmittingContact}
            idPrefix={`supplier-contact-${editingContact?.id || 'new'}-method`}
          />

          <label style={fieldWrapStyle}>
            <span style={labelStyle}>Email</span>
            <input
              name="email"
              type="email"
              value={contactForm.email}
              onChange={(event) => handleContactFormChange('email', event.target.value)}
              disabled={isSubmittingContact}
              style={inputBaseStyle}
            />
          </label>
        </FormModal>
      ) : null}

      <Modal
        isOpen={Boolean(contactPendingDelete)}
        onClose={handleCloseDeleteModal}
        onConfirm={handleConfirmDelete}
        title="Delete Contact"
        description={`Are you sure you want to delete ${contactPendingDelete?.name || 'this contact'}?`}
        confirmLabel={isDeletingContact ? 'Deleting...' : 'Delete'}
        confirmDisabled={isDeletingContact}
        cancelDisabled={isDeletingContact}
      />
    </section>
  );
}
